/**
 * Google Analytics 4 Event Tracking Utility
 * Provides comprehensive e-commerce event tracking for Atlas Palm
 */

// Declare gtag on window
declare global {
    interface Window {
        gtag: (command: string, ...args: unknown[]) => void;
        dataLayer: unknown[];
    }
}

// GA4 Configuration
const GA4_MEASUREMENT_ID = 'G-J6NXB8S4FM';

// Helper to safely call gtag AND send via beacon as backup
const gtag = (eventName: string, eventParams: Record<string, unknown> = {}) => {
    if (typeof window === 'undefined') return;

    console.log('[GA4 Event]', eventName, eventParams);

    // Method 1: Use gtag if available
    if (window.gtag) {
        window.gtag('event', eventName, eventParams);
    }

    // Method 2: Push directly to dataLayer as backup
    if (window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            ...eventParams
        });
    }
};

// ============================================
// PAGE VIEW TRACKING
// ============================================

export const trackPageView = (pagePath: string, pageTitle: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', 'G-J6NXB8S4FM', {
            page_path: pagePath,
            page_title: pageTitle,
        });
    }
};

// ============================================
// E-COMMERCE EVENTS (GA4 Standard)
// ============================================

interface ProductItem {
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
    item_variant?: string;
    item_category?: string;
}

// View Product / Item
export const trackViewItem = (product: {
    id: string;
    name: string;
    price: number;
    category?: string;
    variant?: string;
}) => {
    gtag('view_item', {
        currency: 'MAD',
        value: product.price,
        items: [{
            item_id: product.id,
            item_name: product.name,
            price: product.price,
            item_category: product.category || 'Dates',
            item_variant: product.variant,
        }],
    });
};

// Add to Cart
export const trackAddToCart = (product: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    variant?: string;
    category?: string;
}) => {
    gtag('add_to_cart', {
        currency: 'MAD',
        value: product.price * product.quantity,
        items: [{
            item_id: product.id,
            item_name: product.name,
            price: product.price,
            quantity: product.quantity,
            item_variant: product.variant,
            item_category: product.category || 'Dates',
        }],
    });
};

// Remove from Cart
export const trackRemoveFromCart = (product: {
    id: string;
    name: string;
    price: number;
    quantity: number;
    variant?: string;
}) => {
    gtag('remove_from_cart', {
        currency: 'MAD',
        value: product.price * product.quantity,
        items: [{
            item_id: product.id,
            item_name: product.name,
            price: product.price,
            quantity: product.quantity,
            item_variant: product.variant,
        }],
    });
};

// View Cart
export const trackViewCart = (items: ProductItem[], cartTotal: number) => {
    gtag('view_cart', {
        currency: 'MAD',
        value: cartTotal,
        items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: item.price,
            quantity: item.quantity,
            item_variant: item.item_variant,
        })),
    });
};

// Begin Checkout
export const trackBeginCheckout = (items: ProductItem[], cartTotal: number) => {
    gtag('begin_checkout', {
        currency: 'MAD',
        value: cartTotal,
        items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: item.price,
            quantity: item.quantity,
            item_variant: item.item_variant,
        })),
    });
};

// Add Shipping Info
export const trackAddShippingInfo = (items: ProductItem[], cartTotal: number, shippingMethod: string) => {
    gtag('add_shipping_info', {
        currency: 'MAD',
        value: cartTotal,
        shipping_tier: shippingMethod,
        items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: item.price,
            quantity: item.quantity,
        })),
    });
};

// Purchase Complete
export const trackPurchase = (order: {
    orderId: string;
    total: number;
    shipping: number;
    discount?: number;
    couponCode?: string;
    items: ProductItem[];
    channel: 'website' | 'whatsapp';
}) => {
    gtag('purchase', {
        transaction_id: order.orderId,
        value: order.total,
        currency: 'MAD',
        shipping: order.shipping,
        coupon: order.couponCode,
        items: order.items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: item.price,
            quantity: item.quantity,
            item_variant: item.item_variant,
        })),
    });

    // Also track the channel
    gtag('order_channel', {
        channel: order.channel,
        order_id: order.orderId,
        value: order.total,
    });
};

// ============================================
// CUSTOM EVENTS FOR ATLAS PALM
// ============================================

// WhatsApp Order Initiated (before redirect)
export const trackWhatsAppCheckout = (items: ProductItem[], cartTotal: number) => {
    gtag('whatsapp_checkout', {
        currency: 'MAD',
        value: cartTotal,
        items_count: items.length,
        items: items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            price: item.price,
            quantity: item.quantity,
        })),
    });
};

// Apply Coupon
export const trackApplyCoupon = (couponCode: string, discountValue: number, discountType: 'percentage' | 'fixed') => {
    gtag('apply_coupon', {
        coupon_code: couponCode,
        discount_value: discountValue,
        discount_type: discountType,
    });
};

// Contact Form Submitted
export const trackContactForm = (formType: 'general' | 'wholesale' | 'support') => {
    gtag('contact_form_submit', {
        form_type: formType,
    });
};

// WhatsApp Click (general)
export const trackWhatsAppClick = (source: string) => {
    gtag('whatsapp_click', {
        source: source,
    });
};

// Review Submitted
export const trackReviewSubmit = (rating: number, productId?: string) => {
    gtag('review_submit', {
        rating: rating,
        product_id: productId || 'general',
    });
};

// Search Performed
export const trackSearch = (searchTerm: string) => {
    gtag('search', {
        search_term: searchTerm,
    });
};

// Category Viewed
export const trackViewCategory = (categoryName: string, categoryId: string) => {
    gtag('view_item_list', {
        item_list_id: categoryId,
        item_list_name: categoryName,
    });
};

// Blog Post Viewed
export const trackBlogView = (blogId: string, blogTitle: string) => {
    gtag('blog_view', {
        blog_id: blogId,
        blog_title: blogTitle,
    });
};

// Wholesale Inquiry
export const trackWholesaleInquiry = () => {
    gtag('wholesale_inquiry', {
        event_category: 'engagement',
    });
};

// Newsletter Signup (if applicable)
export const trackNewsletterSignup = (email: string) => {
    gtag('newsletter_signup', {
        method: 'website',
    });
};

// ============================================
// ENGAGEMENT EVENTS
// ============================================

// Share
export const trackShare = (method: string, contentType: string, itemId: string) => {
    gtag('share', {
        method: method,
        content_type: contentType,
        item_id: itemId,
    });
};

// Sign Up
export const trackSignUp = (method: string) => {
    gtag('sign_up', {
        method: method,
    });
};

// Login
export const trackLogin = (method: string) => {
    gtag('login', {
        method: method,
    });
};
